import { linkifyHtml } from '../utils/linkify';
import DOMPurify from 'dompurify';

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSeo } from '../hooks/useSeo';
import { AdminControls } from '../components/AdminControls';
import { PromptGenerator } from '../components/PromptGenerator';
import { Breadcrumbs } from '../components/Breadcrumbs';
import type { PageContent } from '../types';
import { useToast } from '../hooks/useToast';

const kebabToCamel = (s: string): string => {
  return s.replace(/-(\w)/g, (_, c) => c.toUpperCase());
};

export const PromptCategoryPage: React.FC = () => {
  const { categoryKey = '' } = useParams<{ categoryKey: string }>();
  const contentKey = kebabToCamel(categoryKey);

  const { content: allContent, isAdminMode, updatePageContent } = useContent();
  const { showToast } = useToast();

  const [pageContent, setPageContent] = useState<PageContent>(
    allContent[contentKey] || { 
      h1: 'Loading...', 
      body: '', 
      seo: { title: '', description: '', canonical: '', slug: '', customHead: '', noindex: false } 
    }
  );

  const onSeoApplied = useCallback(() => {
    window.dispatchEvent(new Event('app-rendered'));
  }, []);

  useSeo(pageContent, contentKey, onSeoApplied);

  useEffect(() => {
    if (allContent[contentKey]) {
      setPageContent(allContent[contentKey]);
    }
  }, [allContent, contentKey]);

  const handleSave = async () => {
    const oldSlug = allContent[contentKey]?.seo?.slug;
    const redirectCreated = await updatePageContent(contentKey, pageContent);
    showToast(`${pageContent.h1 || 'Page'} content saved!`);
    if (redirectCreated) {
      const newSlug = pageContent.seo.slug;
      showToast(`Redirect created: ${oldSlug} -> ${newSlug}`);
    }
  };
  
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'AI Prompt Generators', path: '/ai-prompt-generator/' },
    { name: pageContent.h1.replace(' Generator', '') }
  ];

  const sanitizedBody = pageContent?.body ? linkifyHtml(pageContent.body) : '';

  return (
    <div className="w-full max-w-6xl mx-auto py-8 md:py-12 px-4 sm:px-6 lg:px-8 text-[#1F2937]">
      <div className="w-full max-w-3xl mx-auto">
        
        <Breadcrumbs crumbs={breadcrumbs} />

        <header className="text-center mb-4">
          <h1 className="text-3xl md:text-4xl font-bold text-[#4F46E5]">
            {pageContent.h1}
          </h1>
        </header>
        <div
          className="text-[#6B7280] prose max-w-none mx-auto mb-6"
          dangerouslySetInnerHTML={{ __html: sanitizedBody }}
        />

        {isAdminMode && (
          <AdminControls
            pageId={contentKey}
            pageContent={pageContent}
            onContentChange={setPageContent}
            onSave={handleSave}
          />
        )}
        
        <PromptGenerator categoryKey={categoryKey} />

      </div>
    </div>
  );
};
